import { useMemo } from "react";
import { NavLink, Outlet, useMatch, useNavigate } from "react-router";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  LayersIcon,
  LogOutIcon,
  MessageSquareIcon,
  PanelLeftIcon,
  ShieldIcon,
  SmartphoneIcon,
  WebhookIcon,
} from "lucide-react";
import { toast } from "sonner";

import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import { cn } from "@/lib/utils";
import { errorMessage } from "@/lib/errors";
import { accountStatusNeedsAttention, deliveryStatus, formatRelative } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";

interface NavItem {
  to: string;
  label: string;
  icon: typeof SmartphoneIcon;
  /** Small count rendered on the right; only shown when non-zero. */
  badge?: number;
  badgeTone?: "danger" | "warning" | "neutral";
}

export function AppShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const setAuthenticated = useUiStore((s) => s.setAuthenticated);
  const sessionExpiresAt = useUiStore((s) => s.sessionExpiresAt);

  // Badge counts only. A failure here is already surfaced by whichever screen
  // owns the data, so the sidebar just shows no badge rather than shouting.
  // Already fetched by App's session check; this reads the same cache entry
  // rather than issuing a second call. Empty when the build was not stamped.
  const buildCommit = useQuery(AdminService.method.whoAmI, {}, { retry: false, staleTime: 30_000 })
    .data?.buildCommit;

  const accountsQuery = useQuery(AdminService.method.listAccounts, {});
  const deliveriesQuery = useQuery(AdminService.method.listWebhookDeliveries, {
    pendingOnly: true,
    pageSize: 200,
  });

  const accounts = accountsQuery.data?.accounts;
  const deliveries = deliveriesQuery.data?.deliveries;

  const logout = useMutation(AdminService.method.logout);

  const items = useMemo<NavItem[]>(() => {
    const needAttention = (accounts ?? []).filter((a) =>
      accountStatusNeedsAttention(a.status),
    ).length;
    const pendingHooks = (deliveries ?? []).filter((d) => deliveryStatus(d) === "pending").length;
    return [
      {
        to: "/accounts",
        label: "Accounts",
        icon: SmartphoneIcon,
        badge: needAttention,
        badgeTone: "danger",
      },
      { to: "/batches", label: "Batches", icon: LayersIcon },
      {
        to: "/webhooks",
        label: "Webhooks",
        icon: WebhookIcon,
        badge: pendingHooks,
        badgeTone: "warning",
      },
      { to: "/sessions", label: "Sessions", icon: ShieldIcon },
    ];
  }, [accounts, deliveries]);

  const expiresAt = sessionExpiresAt ? new Date(sessionExpiresAt) : undefined;

  async function signOut() {
    try {
      await logout.mutateAsync({});
      toast.success("Signed out");
    } catch (err) {
      // The cookie may already be gone server-side; drop the client state
      // either way rather than trapping the operator in a dead session.
      toast.error("Sign-out failed on the server", { description: errorMessage(err) });
    } finally {
      setAuthenticated(false);
      // Every cached response belonged to the session just ended.
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="flex min-h-svh bg-background">
      <aside
        className={cn(
          "sticky top-0 flex h-svh shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground transition-[width] duration-200",
          collapsed ? "w-14" : "w-60",
        )}
      >
        <div
          className={cn(
            "flex h-14 items-center gap-2 px-3",
            collapsed && "justify-center px-0",
          )}
        >
          <MessageSquareIcon className="size-5 shrink-0 text-tone-success" />
          {!collapsed ? (
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-semibold">WABMS</p>
              <p className="truncate text-[11px] text-muted-foreground">WhatsApp batch messages</p>
            </div>
          ) : null}
          {!collapsed && buildCommit ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="tabular ml-auto shrink-0 self-start pt-1 text-[10px] text-muted-foreground">
                  {buildCommit.slice(0, 7)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">Built from commit {buildCommit}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        <Separator />

        <nav className="flex-1 space-y-1 p-2">
          {items.map((item) => (
            <SidebarLink key={item.to} item={item} collapsed={collapsed} />
          ))}
        </nav>

        <Separator />

        <div className={cn("flex items-center gap-1 p-2", collapsed && "flex-col")}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={toggleSidebar} aria-label="Toggle sidebar">
                <PanelLeftIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">{collapsed ? "Expand" : "Collapse"} sidebar</TooltipContent>
          </Tooltip>
          <ThemeToggle />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={logout.isPending}
                onClick={() => void signOut()}
                aria-label="Sign out"
              >
                <LogOutIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              Sign out
              {expiresAt ? ` · session expires ${formatRelative(timestampFromDate(expiresAt))}` : ""}
            </TooltipContent>
          </Tooltip>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="mx-auto w-full max-w-7xl space-y-6 p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  // The active state is resolved here rather than through NavLink's callback
  // form: when collapsed the link becomes a TooltipTrigger child, and Radix's
  // Slot stringifies a function className instead of calling it, which drops
  // every class on the element.
  const isActive = useMatch({ path: item.to, end: false }) !== null;
  const link = (
    <NavLink
      to={item.to}
      className={cn(
        "flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm transition-colors",
        isActive
          ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
          : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
        collapsed && "justify-center px-0",
      )}
    >
      <Icon className="size-4 shrink-0" />
      {!collapsed ? <span className="flex-1 truncate">{item.label}</span> : null}
      {!collapsed && item.badge ? (
        <span
          className={cn(
            "tabular rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
            item.badgeTone === "danger" && "bg-tone-danger/15 text-tone-danger",
            item.badgeTone === "warning" && "bg-tone-warning/15 text-tone-warning",
            item.badgeTone === "neutral" && "bg-muted text-muted-foreground",
          )}
        >
          {item.badge}
        </span>
      ) : null}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">
        {item.label}
        {item.badge ? ` (${item.badge})` : ""}
      </TooltipContent>
    </Tooltip>
  );
}
